import { Collapse } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMaterialStateLabel, materialStateEnum } from '../../../../constants';
import { useCreateDailyTransferRideMutation, useUpdateDailyTransferRideMutation } from '../../../../hooks/mutations/useDailyTransferRideMutation';
import { useMachinesQuery } from '../../../../hooks/queries/useMachinesQuery';
import { useQuarriesQuery } from '../../../../hooks/queries/useQuarriesQuery';
import { useRentalsQuery } from '../../../../hooks/queries/useRentalsQuery';
import { useSitesQuery } from '../../../../hooks/queries/useSitesQuery';
import { useToolsQuery } from '../../../../hooks/queries/useToolsQuery';
import { DailyTransferRide } from '../../../../services/apiSLTP/dailyReports/dailyTransfersRides';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ModalBody from '../../ModalBody';

interface ModalProps {
    open: boolean;
    handleClose: () => void;
    refetch: () => void;
    transfer: DailyTransferRide | null;
    truckRideId: string;
    siteName: string;
}

const ManageDailyTransferRideModal: React.FC<ModalProps> = ({ open, handleClose, refetch, transfer, truckRideId, siteName }) => {
    const { t } = useTranslation();
    const [currentTransferRideId, setCurrentTransferRideId] = useState(transfer?._id || '');

    const [selectedDestination, setSelectedDestination] = useState<OptionType | null>(null);
    const [selectedMachine, setSelectedMachine] = useState<OptionType | null>(null);
    const [selectedTool, setSelectedTool] = useState<OptionType | null>(null);
    const [selectedRental, setSelectedRental] = useState<OptionType | null>(null);
    const [rentalName, setRentalName] = useState('');

    const [selectedType, setSelectedType] = useState<OptionType | null>(null);

    const [selectedState, setSelectedState] = useState<OptionType | null>(null);

    const states = [
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.NEW))), value: materialStateEnum.NEW.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.REMOVE))), value: materialStateEnum.REMOVE.toString() },
    ];

    const {
        data: dataSites,
        isLoading: isLoadingSites,
        isError: isErrorSites,
        isSuccess: isSuccessSites,
        refetch: refetchSites,
    } = useSitesQuery({ queries: { active: true } });

    const {
        data: dataMachines,
        isLoading: isLoadingMachines,
        isError: isErrorMachines,
        isSuccess: isSuccessMachines,
        refetch: refetchMachines,
    } = useMachinesQuery({ queries: { active: true } });

    const {
        data: dataQuarries,
        isLoading: isLoadinQuarries,
        isError: isErrorQuarries,
        isSuccess: isSuccessQuarries,
        refetch: refetchQuarries,
    } = useQuarriesQuery({ queries: { active: true } });

    const {
        data: dataTools,
        isLoading: isLoadingTools,
        isError: isErrorTools,
        isSuccess: isSuccessTools,
        refetch: refetchTools,
    } = useToolsQuery({ queries: { active: true } });

    const {
        data: dataRentals,
        isLoading: isLoadingRentals,
        isError: isErrorRentals,
        isSuccess: isSuccessRentals,
        refetch: refetchRentals,
    } = useRentalsQuery({ queries: { active: true } });

    const {
        mutate: createTransferRide,
        isSuccess: isCreateTransferRideSuccess,
        isError: isCreateTransferRideError,
        isLoading: isCreateTransferRideLoading,
        error: createTransferRideError,
    } = useCreateDailyTransferRideMutation({
        onSuccess: (data, variables) => {
            setCurrentTransferRideId(data.data._id);
            refetch();
        },
    });

    const {
        mutate: updateTransferRide,
        isSuccess: isUpdateTransferRideSuccess,
        isError: isUpdateTransferRideError,
        isLoading: isUpdateTransferRideLoading,
        error: updateTransferRideError,
    } = useUpdateDailyTransferRideMutation({
        onSuccess: (data, variables) => {
            refetch();
        },
    });

    const postDailyTransferRide = async () => {
        if (!selectedState) return;
        createTransferRide({
            dailyTrucksRidesId: truckRideId,
            state: parseInt(selectedState.value),
            machineId: selectedMachine?.value,
            toolId: selectedTool?.value,
            rentalCompanyId: selectedRental?.value,
            rentalName: rentalName,
            quarryId: selectedDestination?.group === capitalizeFirstLetter(t('quarries')) ? selectedDestination.value : undefined,
            siteId: selectedDestination?.group === capitalizeFirstLetter(t('sites')) ? selectedDestination.value : undefined,
        });
    };

    const patchDailyTransferRide = async () => {
        updateTransferRide({
            DailyTransferRideId: currentTransferRideId,
            data: {
                state: selectedState ? parseInt(selectedState.value) : undefined,
                machineId: selectedMachine?.value || null,
                toolId: selectedTool?.value || null,
                rentalCompanyId: selectedRental?.value || null,
                rentalName: rentalName,
                quarryId: selectedDestination?.group === capitalizeFirstLetter(t('quarries')) ? selectedDestination.value : null,
                siteId: selectedDestination?.group === capitalizeFirstLetter(t('sites')) ? selectedDestination.value : null,
            },
        });
    };

    useEffect(() => {
        if (transfer?.siteId?._id)
            setSelectedDestination({ label: transfer?.siteId?.name, value: transfer?.siteId?._id, group: capitalizeFirstLetter(t('sites')) });
        else if (transfer?.quarryId?._id)
            setSelectedDestination({
                label: transfer?.quarryId?.designation,
                value: transfer?.quarryId?._id,
                group: capitalizeFirstLetter(t('quarries')),
            });
        if (transfer?.machineId?._id) {
            setSelectedMachine({ label: transfer?.machineId?.designation, value: transfer?.machineId?._id });
            setSelectedType({ label: capitalizeFirstLetter(t('machine')), value: '0' });
        } else if (transfer?.toolId?._id) {
            setSelectedTool({ label: transfer?.toolId?.designation, value: transfer?.toolId?._id });
            setSelectedType({ label: capitalizeFirstLetter(t('tool')), value: '1' });
        } else if (transfer?.rentalCompanyId?._id || transfer?.rentalName) {
            setSelectedRental({ label: transfer?.rentalCompanyId?.designation || '', value: transfer?.rentalCompanyId?._id || '' });
            setRentalName(transfer?.rentalName || '');
            setSelectedType({ label: capitalizeFirstLetter(t('rental')), value: '2' });
        }
        if (transfer)
            setSelectedState({ label: capitalizeFirstLetter(t(getMaterialStateLabel(transfer?.state))), value: transfer?.state.toString() });
    }, []);

    return (
        <ModalBody
            title={t('transfer')}
            subtitle={!!currentTransferRideId.length ? t('update') : t('add')}
            description={siteName}
            disableSubmit={!selectedState?.value.length}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                !!currentTransferRideId.length ? patchDailyTransferRide() : postDailyTransferRide();
            }}
        >
            <DropDown
                selectedItem={selectedState}
                items={states}
                handleChange={(event, value) => setSelectedState(value)}
                inputLabel={t('state')}
                required
            />
            <DropDown
                selectedItem={selectedDestination}
                items={
                    dataSites
                        ?.map((site) => ({ label: site.name, value: site._id, group: capitalizeFirstLetter(t('sites')) }))
                        .concat(
                            dataQuarries?.map((quarry) => ({
                                label: quarry.designation,
                                value: quarry._id,
                                group: capitalizeFirstLetter(t('quarries')),
                            })) || []
                        ) || []
                }
                handleChange={(event, value) => setSelectedDestination(value)}
                inputLabel={selectedState?.value ? (parseInt(selectedState?.value) === materialStateEnum.NEW ? t('from') : t('to')) : t('place')}
                grouped
                required
            />

            <DropDown
                selectedItem={selectedType}
                items={[t('machine'), t('tool'), t('rental')]?.map((type, index) => ({
                    label: capitalizeFirstLetter(type),
                    value: index.toString(),
                }))}
                handleChange={(event, value) => {
                    setSelectedType(value);
                    setSelectedMachine(null);
                    setSelectedTool(null);
                    setSelectedRental(null);
                    setRentalName('');
                }}
                inputLabel={t('type')}
                sortItems={false}
                required
            />
            <Collapse in={selectedType?.value === '0'}>
                <div className="fx-c gap-5px">
                    <DropDown
                        selectedItem={selectedMachine}
                        items={dataMachines?.map((machine) => ({ label: machine.designation, value: machine._id })) || []}
                        handleChange={(event, value) => {
                            setSelectedMachine(value);
                            setSelectedTool(null);
                            setSelectedRental(null);
                            setRentalName('');
                        }}
                        inputLabel={t('machine')}
                        required
                    />
                </div>
            </Collapse>

            <Collapse in={selectedType?.value === '1'}>
                <div className="fx-c gap-5px">
                    <DropDown
                        selectedItem={selectedTool}
                        items={dataTools?.map((tool) => ({ label: tool.designation, value: tool._id })) || []}
                        handleChange={(event, value) => {
                            setSelectedTool(value);
                            setSelectedMachine(null);
                            setSelectedRental(null);
                            setRentalName('');
                        }}
                        inputLabel={t('tool')}
                        required
                    />
                </div>
            </Collapse>

            <Collapse in={selectedType?.value === '2'}>
                <div className="fx-c gap-5px">
                    <DropDown
                        selectedItem={selectedRental}
                        items={dataRentals?.map((rental) => ({ label: rental.designation, value: rental._id })) || []}
                        handleChange={(event, value) => {
                            setSelectedRental(value);
                            setSelectedMachine(null);
                            setSelectedTool(null);
                        }}
                        inputLabel={t('rental')}
                        required
                    />
                    <CustomTextInput value={rentalName} onChange={(text) => setRentalName(text)} label={t('name')} />
                </div>
            </Collapse>
        </ModalBody>
    );
};

export default ManageDailyTransferRideModal;
